/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Snackbar,
    Alert,
    Tooltip,
    IconButton,
    Stack,
    TextField,
    Button,
    Box,
    Typography,
    Skeleton,
    Badge,
    InputAdornment
} from '@mui/material';
import { FilterList, Search } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

import FilterDrawer from 'components/common/FilterDrawer';
import ProductRow from './ProductRow';
import ProductSkeletonRow from './ProductSkeletonRow';
import EmptyState from './EmptyState';
import LocationWarningDialog from '../settings/LocationWarningDialog';
// import DeleteDialog from './DeleteDialog';
import { useProductCategories } from 'hooks/useProductCategories';
import { useAllProducts, useDeleteProduct } from 'hooks/useProduct';
import { useCurrentVendor } from 'hooks/useUser';

export default function ProductsTable() {
    const navigate = useNavigate();
    const location = useLocation();

    // -------------------------------------------------------------------------
    // STATE: General & Filter
    // -------------------------------------------------------------------------
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [search, setSearch] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [isAvailable, setIsAvailable] = useState(null);

    // -------------------------------------------------------------------------
    // STATE: Deletion & Feedback
    // -------------------------------------------------------------------------
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [selectedProductId, setSelectedProductId] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });
    const [showDialog, setShowDialog] = useState(false);

    // -------------------------------------------------------------------------
    // IMPORTED DATA
    // -------------------------------------------------------------------------
    const { data: categoryResponse } = useProductCategories({ limit: 1000 });
    const categories = categoryResponse?.data || [];

    const deleteMutation = useDeleteProduct();

    const { data, isLoading, error, refetch } = useAllProducts({
        page: page + 1,
        limit: rowsPerPage,
        search: search || undefined,
        available: isAvailable,
        categoryId: filters.categoryId !== 'All' ? filters.categoryId : undefined,
        minPrice: filters.minPrice || undefined,
        maxPrice: filters.maxPrice || undefined,
        ratings: filters.ratings || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        sortBy: filters.sortBy || 'createdAt',
        sortOrder: filters.sortOrder || 'desc',
        status: filters.status !== 'All' ? filters.status : undefined
    });
    const { data: vendorData } = useCurrentVendor();

    const products = data?.data || [];

    // -------------------------------------------------------------------------
    // EFFECTS
    // -------------------------------------------------------------------------
    useEffect(() => {
        if (location.state?.snackbar) {
            setSnackbar({
                open: true,
                message: location.state.snackbar.message,
                severity: location.state.snackbar.severity
            });
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    useEffect(() => {
        if (error?.response?.data?.code === 'VENDOR_LOCATION_NOT_SET') {
            setShowDialog(true);
        }
    }, [error]);

    // -------------------------------------------------------------------------
    // HANDLERS
    // -------------------------------------------------------------------------
    const handleFilterApply = (newFilters) => {
        setFilters(newFilters);

        if (newFilters.status === 'All' || !newFilters.status) {
            setIsAvailable(null);
        } else if (newFilters.status === 'available') {
            setIsAvailable(true);
        } else if (newFilters.status === 'draft') {
            setIsAvailable(false);
        }

        setPage(0);
    };

    const handleDeleteClick = (id) => {
        if (window.confirm('Are you sure you want to delete this product?')) {
            deleteMutation.mutate(id, {
                onSuccess: () => {
                    setSnackbar({ open: true, message: 'Product deleted successfully', severity: 'success' });
                    setSelectedProductId(null);
                    refetch();
                },
                onError: (err) => {
                    setSnackbar({ open: true, message: err.message || 'Failed to delete', severity: 'error' });
                }
            });
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleAddProductClick = () => {
        const vendor = vendorData?.vendor;
        if (!vendor?.location || !vendor?.location?.coordinates || vendor?.location?.coordinates?.length !== 2) {
            setShowDialog(true);
        } else {
            navigate('/vendor/products/create');
        }
    };

    // -------------------------------------------------------------------------
    // FILTER CONFIG
    // -------------------------------------------------------------------------
    const filterConfig = [
        {
            name: 'categoryId',
            label: 'Category',
            type: 'select',
            options: categories.map((c) => ({ label: c.name, value: c._id }))
        },
        {
            name: 'status',
            label: 'Status',
            type: 'select',
            options: [
                { label: 'Available', value: 'available' },
                { label: 'Draft', value: 'draft' }
            ]
        },
        {
            name: 'ratings',
            label: 'Min Rating',
            type: 'select',
            options: [
                { label: '1 Star & Up', value: '1' },
                { label: '2 Stars & Up', value: '2' },
                { label: '3 Stars & Up', value: '3' },
                { label: '4 Stars & Up', value: '4' },
                { label: '5 Stars', value: '5' }
            ]
        },
        { name: 'minPrice', label: 'Min Price', type: 'number' },
        { name: 'maxPrice', label: 'Max Price', type: 'number' },
        { name: 'startDate', label: 'Start Date', type: 'date' },
        { name: 'endDate', label: 'End Date', type: 'date' },
        {
            name: 'sortBy',
            label: 'Sort By',
            type: 'select',
            options: [
                { label: 'Date Created', value: 'createdAt' },
                { label: 'Price', value: 'price' },
                { label: 'Name', value: 'name' },
                { label: 'Rating', value: 'averageRating' },
                { label: 'Stock', value: 'stock' }
            ]
        },
        {
            name: 'sortOrder',
            label: 'Sort Order',
            type: 'select',
            options: [
                { label: 'Ascending', value: 'asc' },
                { label: 'Descending', value: 'desc' }
            ]
        }
    ];

    if (showDialog) {
        return <LocationWarningDialog open={showDialog} onClose={() => setShowDialog(false)} />;
    }

    return (
        <Paper sx={{ width: '100%', p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                <TextField
                    placeholder="Search Products..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        )
                    }}
                />

                <Stack direction="row" spacing={1} alignItems="center">
                    <Badge badgeContent={Object.keys(filters).length} color="primary">
                        <Button variant="outlined" startIcon={<FilterList />} onClick={() => setFilterOpen(true)}>
                            Filters
                        </Button>
                    </Badge>
                    {Object.keys(filters).length > 0 && (
                        <Button variant="text" onClick={() => setFilters({})} color="error" size="small">
                            Reset
                        </Button>
                    )}
                </Stack>
                <Box sx={{ flexGrow: 1 }} />
                <Button variant="contained" onClick={handleAddProductClick}>
                    Add New Product
                </Button>
            </Stack>

            <FilterDrawer
                open={filterOpen}
                onClose={() => setFilterOpen(false)}
                onApply={handleFilterApply}
                initialFilters={filters}
                config={filterConfig}
            />

            {!isLoading && !error && products.length === 0 ? (
                <EmptyState onCreate={handleAddProductClick} />
            ) : (
                <>
                    <TableContainer>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Price</TableCell>
                                    <TableCell>Stock</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {isLoading && [...Array(rowsPerPage)].map((_, i) => <ProductSkeletonRow key={i} />)}

                                {!isLoading && error && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            Error loading products
                                        </TableCell>
                                    </TableRow>
                                )}

                                {!isLoading &&
                                    products.map((p) => (
                                        <ProductRow key={p.id} product={p} onDelete={() => handleDeleteClick(p?._id || p?.id)} />
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={data?.total || 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
}
